import React from 'react';
import styled from 'styled-components';
import { useCareer } from '../../shared/hooks/useApi/useCareer';
import { AlertPosition, AlertType, CareerPeriodType, CareerType, DividerType, ModalType } from '../../shared/enum/EnumRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useAboutViewStore } from '../../shared/store/aboutViewStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';
import reorderIcon from '../../asset/icon/reorder.png';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import Divider from '../../shared/Divider';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { toUpdateCareerBulkItemReq } from '../../shared/util/normalizeCareer';
import { isValidHttpUrl } from '../../shared/util/urlValidation';
import CareerInfo from './CareerInfo';

interface CareerSectionProps {
  title: string;
  type: CareerType;
  storageRange: CareerPeriodType;
  displayRange: CareerPeriodType;
}

const CareerSection: React.FC<CareerSectionProps> = ({
  title,
  type,
  storageRange,
  displayRange,
}) => {
  const { isEditMode } = useEditMode();
  const { isExtended } = useAboutViewStore();
  const { careerList, updateCareerBulk } = useCareer();
  const { setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { deviceType } = useDeviceStore();
  const filteredCareers = careerList.filter((each) => each.careerType === type);
  const hasChangedCareer = filteredCareers.some((career) => career.hasChanged);
  const usePeriodInfo =
    displayRange === CareerPeriodType.PERIOD ||
    (isExtended && storageRange === CareerPeriodType.PERIOD);

  if (filteredCareers.length <= 0) {
    return null;
  }

  const handleReOrder = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Career",
      value: type,
      handleChange: () => { },
    });
  }

  const handleUpdateSection = async () => {
    if (!hasChangedCareer) return;

    if (filteredCareers.some((career) => !isValidHttpUrl(career.relatedUrl))) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      return;
    }

    await updateCareerBulk({
      careerType: type,
      careerList: filteredCareers.map(toUpdateCareerBulkItemReq),
    });
  };

  return (
    <Section $isMobile={deviceType === DeviceType.MOBILE}>
      <TitleContainer
        data-has-changed={hasChangedCareer ? "true" : undefined}
      >
        <CareerTitle>{title}</CareerTitle>
        {isEditMode && (
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            backgroundImg={reorderIcon}
            onClick={handleReOrder}
          />
        )}
        {isEditMode && hasChangedCareer && (
          <ConfirmBtnWrapper>
            <UniversalIconBtn
              isPrimary={true}
              size="fit"
              border={false}
              backgroundImg={confirmIcon}
              backgroundImgHover={confirmIcon2}
              onClick={handleUpdateSection}
            />
          </ConfirmBtnWrapper>
        )}
      </TitleContainer>
      <Divider dividerType={DividerType.PLAIN} bottom={'8px'} />
      {filteredCareers.map((each) => (
        <CareerInfo
          key={each.id}
          data={each}
          displayAsPeriod={
            usePeriodInfo && each.periodType === CareerPeriodType.PERIOD
          }
        />
      ))}
    </Section>
  );
}

const Section = styled.div<{ $isMobile: boolean }>`
  position: relative;
  width: ${({ $isMobile }) => $isMobile ? '100%' : 'clamp(320px, 65vw, 600px)'};
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2vw;
  margin-bottom: 4px;
`;

const CareerTitle = styled.h3`
  ${({ theme }) => theme.typography.Body_04};
`;

const ConfirmBtnWrapper = styled.div`
  margin-left: auto;
`;

export default CareerSection;
